import { useEffect, useState } from "react";
import { controlToastState } from "risa-oasis-ui_v2";

import { PharmaStpFileModel } from "../../../../data-model/nycbsPharmaOrder";
import {
  CallingHistoryLog,
  deleteCallingHistoryLog,
  fetchCallingHistoryLogs,
} from "../../../../pages/callingWorklist/services/callingHistoryService";
import { logError } from "../../../../utils/customLogger";
import HistoryEntryCard from "../components/historyEntryCard";

interface HistoryPaneProps {
  data: PharmaStpFileModel;
  /** Drops the outer padding when the parent already provides it. */
  disablePadding?: boolean;
}

/**
 * Identity of one entry across the three merged history collections, where a
 * document id alone is not guaranteed to be unique.
 *
 * @input entry - History entry to key
 * @return Key combining the entry's source collection and document id
 */
const getEntryKey = (entry: CallingHistoryLog): string =>
  `${entry.collection_path ?? ""}/${entry.id ?? ""}`;

/**
 * History tab of the non-EPA modal. Lists every saved call and fax summary for
 * the order, newest first, in a vertically scrolling column, and lets the user
 * delete an entry straight from Firestore.
 *
 * @param props.data - Order row; `identifier` keys the history lookup
 */
const HistoryPane = (props: HistoryPaneProps) => {
  const [logs, setLogs] = useState<CallingHistoryLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  useEffect(() => {
    const identifier = props.data?.identifier;
    if (!identifier) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchCallingHistoryLogs(identifier).then((entries) => {
      setLogs(entries);
      setIsLoading(false);
    });
  }, [props.data?.identifier]);

  const handleDelete = async (entry: CallingHistoryLog) => {
    const entryKey = getEntryKey(entry);
    setDeletingKey(entryKey);

    try {
      await deleteCallingHistoryLog(entry);
      setLogs((previous) =>
        previous.filter((log) => getEntryKey(log) !== entryKey),
      );
      controlToastState("history-log-delete-success");
    } catch (error) {
      logError(error as Error, "Error deleting calling history log");
      controlToastState("history-log-delete-error");
    }

    setDeletingKey(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-primaryGray-12 border-t-primaryGray-4"
          aria-hidden
        />
        <p className="m-0 text-small text-primaryGray-6">Loading history…</p>
      </div>
    );
  }

  const listPadding = props.disablePadding ? "p-0" : "p-5";
  const emptyStateMargin = props.disablePadding ? "" : "m-5";

  if (logs.length === 0) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-primaryGray-15 py-14 text-center ${emptyStateMargin}`}
      >
        <div className="m-0 text-small font-semibold text-primaryGray-1">
          No history yet
        </div>
        <p className="m-0 text-tiny text-primaryGray-6">
          Saved call and fax summaries for this order will show up here.
        </p>
      </div>
    );
  }

  return (
    <ul className={`m-0 flex list-none flex-col gap-4 ${listPadding}`}>
      {logs.map((entry, index) => {
        const isDeletable = Boolean(entry.id && entry.collection_path);

        return (
          <HistoryEntryCard
            key={entry.id ?? index}
            entry={entry}
            fallbackSecondStpStatus={props.data?.second_stp_status ?? null}
            onDelete={isDeletable ? () => handleDelete(entry) : undefined}
            isDeleting={deletingKey === getEntryKey(entry)}
          />
        );
      })}
    </ul>
  );
};

export default HistoryPane;
